// MUST stay the first import — see src/lib/zod-csp.ts (Zod JIT CSP probe).
import "./lib/zod-csp";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import { initRuntimeReporter } from "@plerity/runtime-reporter";

// Phase 7: production runtime-error reporter — captures window.error +
// unhandledrejection and POSTs to /api/observability/errors.
// Disabled in dev (the test-time helper handles E2E specs).
initRuntimeReporter({
  app: "social",
  disabled: import.meta.env.MODE !== "production",
  buildSha: import.meta.env.VITE_BUILD_SHA ?? "dev",
});

/**
 * Register X-Tenant-Id interceptor on the shared dashboard client.
 *
 * The dashboard-shared hooks (useApiQuery, usePaginatedQuery) use their own
 * internal client which doesn't know about the Social app's admin org
 * switcher. Without this interceptor, switching orgs in the dropdown only
 * affects mutations (which use the Social app's local client) — queries
 * continue to fetch data for the default org resolved from hostname.
 *
 * Imported aliased as `sharedApiClient`: this is a *request*-interceptor
 * registration, not an API response site, so the apps-zod-response-validation
 * scanner must not flag main.tsx (it has no response shape to guard). The
 * alias keeps the bare client-dot-access token out of this file (the ops /
 * reviews / calls posture for the same shared-client interceptor pattern).
 */
import { apiClient as sharedApiClient } from "@plerity/dashboard-shared/hooks";
import { PORTAL_URLS } from "@plerity/dashboard-shell";
import { client as localApiClient } from "./api/client";
import { getEffectiveTenantId } from "./lib/admin";
import { installAuthRedirectInterceptor } from "./lib/auth-redirect";

sharedApiClient.interceptors.request.use((config) => {
  const selectedOrgId = getEffectiveTenantId();
  if (selectedOrgId !== null) {
    config.headers.set("X-Tenant-Id", String(selectedOrgId));
  }
  return config;
});

/**
 * Client-side 401 handling — stop the refetch storm, redirect to Hub login.
 *
 * When the session (access + refresh cookies) has fully expired mid-session,
 * every social-api data endpoint returns 401. Without this, the consolidated
 * hub pages' 6+ TanStack Query keys each 401 with no escape hatch, hammering
 * the API (18% of requests → SecurityCFAccessAuthFailureSpike +
 * SecurityBruteForceAuthFailureBurst prod alerts, 2026-07-18). The interceptor
 * redirects to Hub login on the FIRST 401 (idempotent — a module-level guard
 * makes every subsequent concurrent 401 a no-op) and swallows the error so no
 * downstream toast/retry fires during the redirect window. Installed on BOTH
 * clients: the shared dashboard client — queries via the shared query/mutation hooks — and
 * the app's local client (mutations, typed calls). Retry on auth failures is
 * already disabled upstream by `createStandardQueryClient`, so navigation
 * alone stops the storm. See src/lib/auth-redirect.ts for the full rationale.
 */
installAuthRedirectInterceptor(sharedApiClient, PORTAL_URLS.LOGIN);
installAuthRedirectInterceptor(localApiClient, PORTAL_URLS.LOGIN);

const rootEl = document.getElementById("root");
if (rootEl)
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
